import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook, faClock, faFlag, faCheckCircle, faTimesCircle,
  faChevronLeft, faChevronRight, faArrowLeft, faArrowRight, faRedo,
  faLightbulb, faGraduationCap, faInfoCircle, faTrophy, faLock, faCrown,
  faChevronDown, faChevronUp, faList, faChartBar, faAward, faChartLine, faStopwatch,
  faQuestionCircle, faHistory, faFire, faLanguage, faGlobe, faRocket, faEdit
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import UpgradeWall from '../components/UpgradeWall';
import SEO from '../components/SEO';
import { getExamUrl, slugify } from '../utils/slugify';
import MarkdownRenderer from '../components/MarkdownRenderer';

interface Question {
  id: number;
  question_text: string;
  options: { [key: string]: string };
  correct_answer_key?: string; // For backward compatibility
  correct_answer_keys?: string[]; // New: array of correct answers
  explanation?: string;
  topic: string;
  difficulty: number;
}

interface Exam {
  id: number;
  name: string;
  description: string;
  exam_duration_minutes?: number;
  exam_question_count?: number;
  practice_mode_enabled?: boolean;
  passing_score?: number;
  max_score?: number;
  badge_image?: string;
  exam_details?: string;
}

const ExamPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { examId, examName } = useParams<{ examId: string; examName?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGuest = !user;
  const [exam, setExam] = useState<Exam | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [mode, setMode] = useState<'PRACTICE' | 'EXAM' | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: string | string[] }>({});
  const [flaggedQuestions, setFlaggedQuestions] = useState<Set<number>>(new Set());
  const [showResult, setShowResult] = useState(false);
  const [answerFilter, setAnswerFilter] = useState<'all' | 'correct' | 'incorrect'>('all');
  const [result, setResult] = useState<any>(null);
  const [resultQuestionIndex, setResultQuestionIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<{ [key: number]: { correct: boolean; explanation: string } }>({});
  const [loading, setLoading] = useState(true);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [showUpgradeWall, setShowUpgradeWall] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<'exam_mode' | 'no_free_tier' | 'exhausted_free' | 'premium_only'>('premium_only');
  const [freeQuestionsExhausted, setFreeQuestionsExhausted] = useState(false);
  const [hasFreeTier, setHasFreeTier] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<{ end_date: string } | null>(null);
  const [showQuestionNavigator, setShowQuestionNavigator] = useState(false);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [showEnglish, setShowEnglish] = useState(false); // Toggle to show English instead of translated language
  const [englishQuestions, setEnglishQuestions] = useState<Question[]>([]); // Store English questions separately
  const [freeModeEnabled, setFreeModeEnabled] = useState(false); // Free mode status

  useEffect(() => {
    fetchExam();
  }, [examId]);

  // Refresh exam data when language changes to get translated content
  useEffect(() => {
    if (examId) {
      fetchExam();
    }
  }, [i18n.language, examId]);

  // Sync question index when switching languages to keep the same question
  useEffect(() => {
    if (questions.length > 0 && englishQuestions.length > 0 && mode === 'PRACTICE') {
      const currentQuestionId = questions[currentQuestionIndex]?.id;
      if (currentQuestionId) {
        const englishIndex = englishQuestions.findIndex(q => q.id === currentQuestionId);
        if (englishIndex !== -1 && showEnglish && englishIndex !== currentQuestionIndex) {
          // Don't update state here, we'll handle it in the display logic
        }
      }
    }
  }, [showEnglish, questions, englishQuestions, mode, currentQuestionIndex]);

  // Hide upgrade wall when user gets a subscription
  useEffect(() => {
    if (hasSubscription && showUpgradeWall) {
      console.log('Subscription detected - hiding upgrade wall');
      setShowUpgradeWall(false);
    }
  }, [hasSubscription, showUpgradeWall]);

  // Reset result question index when filter changes
  useEffect(() => {
    setResultQuestionIndex(0);
  }, [answerFilter]);

  // Ensure resultQuestionIndex is within bounds when result or filter changes
  useEffect(() => {
    if (!showResult || !result) return;
    
    const filteredDetails = result.details.filter((detail: any) => {
      if (answerFilter === 'all') return true;
      if (answerFilter === 'correct') return detail.correct === true;
      if (answerFilter === 'incorrect') return detail.correct === false;
      return true;
    });
    
    // Use functional update to check current value without adding to dependencies
    setResultQuestionIndex((currentIndex) => {
      if (filteredDetails.length > 0 && currentIndex >= filteredDetails.length) {
        return 0;
      }
      return currentIndex;
    });
  }, [showResult, result, answerFilter]);

  // Determine RTL support - declare early so it's available throughout the component
  const isPageRTL = i18n.language === 'ar';
  const rtlDir = isPageRTL ? 'rtl' : 'ltr';

  // Keyboard navigation for result page
  useEffect(() => {
    if (!showResult || !result) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      const filteredDetails = result.details.filter((detail: any) => {
        if (answerFilter === 'all') return true;
        if (answerFilter === 'correct') return detail.correct === true;
        if (answerFilter === 'incorrect') return detail.correct === false;
        return true;
      });

      if (e.key === 'ArrowLeft' && resultQuestionIndex > 0) {
        setResultQuestionIndex(resultQuestionIndex - 1);
        // Scroll to question review section
        setTimeout(() => {
          const element = document.getElementById('question-review-section');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else if (e.key === 'ArrowRight' && resultQuestionIndex < filteredDetails.length - 1) {
        setResultQuestionIndex(resultQuestionIndex + 1);
        // Scroll to question review section
        setTimeout(() => {
          const element = document.getElementById('question-review-section');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showResult, result, resultQuestionIndex, answerFilter]);

  useEffect(() => {
    // Timer runs in EXAM mode or PRACTICE mode with exam-like settings enabled
    const shouldRunTimer = (mode === 'EXAM' || (mode === 'PRACTICE' && exam?.practice_mode_enabled));
    
    if (shouldRunTimer && timeRemaining !== null && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev === null || prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [mode, timeRemaining, exam?.practice_mode_enabled]);

  const fetchExam = async () => {
    try {
      // Check free mode status first
      let freeMode = false;
      try {
        const freeModeResponse = await api.get('/settings/free-mode/status');
        freeMode = freeModeResponse.data.freeModeEnabled || false;
        setFreeModeEnabled(freeMode);
      } catch (error) {
        console.error('Failed to fetch free mode status:', error);
        // Continue with free mode disabled if check fails
      }

      const response = await api.get(`/exams/${examId}`);
      const examData = response.data.exam;
      setExam(examData);
      setHasFreeTier(examData.is_free_available);
      
      // Redirect to SEO-friendly URL if name is missing or doesn't match
      if (examData.name) {
        const expectedSlug = slugify(examData.name);
        if (!examName || examName !== expectedSlug) {
          navigate(getExamUrl(examData.id, examData.name), { replace: true });
          return;
        }
      }
      
      // If free mode is enabled, grant access to everyone
      if (freeMode) {
        setHasSubscription(true);
        setShowUpgradeWall(false);
        // Still fetch activity if user is logged in
        if (user) {
          try {
            const activityResponse = await api.get(`/stats/exam/${examId}/activity`).catch(() => ({ data: { recentActivity: [] } }));
            setRecentActivity(activityResponse.data.recentActivity || []);
          } catch (error) {
            console.error('Failed to fetch activity:', error);
          }
        }
        return;
      }
      
      // Check subscription status if logged in (only if free mode is disabled)
      if (user) {
        try {
          const [subResponse, activityResponse] = await Promise.all([
            api.get(`/subscriptions/${examId}/status`),
            api.get(`/stats/exam/${examId}/activity`).catch(() => ({ data: { recentActivity: [] } }))
          ]);
          const hasActiveSub = subResponse.data.hasActiveSubscription;
          setHasSubscription(hasActiveSub);
          setSubscriptionDetails(subResponse.data.subscription);
          setRecentActivity(activityResponse.data.recentActivity || []);
          console.log('Subscription check in fetchExam:', hasActiveSub);
          
          // Check if user is admin
          const isAdmin = user.isAdmin === true;
          
          // Only show upgrade wall if exam has no free tier AND user has no subscription AND not admin
          if (!response.data.exam.is_free_available && !hasActiveSub && !isAdmin) {
            setUpgradeReason('no_free_tier');
            setShowUpgradeWall(true);
          } else {
            // User has subscription, exam has free tier, or is admin - hide upgrade wall
            setShowUpgradeWall(false);
          }
        } catch (error) {
          console.error('Subscription check error in fetchExam:', error);
          // If check fails, assume no subscription
          setHasSubscription(false);
          const isAdmin = user.isAdmin === true;
          if (!response.data.exam.is_free_available && !isAdmin) {
            setUpgradeReason('no_free_tier');
            setShowUpgradeWall(true);
          } else {
            setShowUpgradeWall(false);
          }
        }
      } else if (!user && !response.data.exam.is_free_available) {
        // Guest user, no free tier
        setUpgradeReason('no_free_tier');
        setShowUpgradeWall(true);
      } else {
        // Guest user with free tier - hide upgrade wall
        setShowUpgradeWall(false);
      }
    } catch (error) {
      console.error('Failed to fetch exam:', error);
    }
  };

  const startExam = async (selectedMode: 'PRACTICE' | 'EXAM', questionLimit: number = 20) => {
    console.log('startExam called:', { selectedMode, user: !!user, hasSubscription, isGuest, isAdmin: user?.isAdmin });
    
    // Check if user is admin
    const isAdmin = user?.isAdmin === true;
    
    // Declare currentHasSubscription at function scope
    let currentHasSubscription = hasSubscription;
    
    // If free mode is enabled, grant access to everyone
    if (freeModeEnabled) {
      currentHasSubscription = true;
      setHasSubscription(true);
      setShowUpgradeWall(false);
      // Continue to start exam
    } else {
      // Refresh subscription status if user is logged in
      if (user) {
        try {
          const subResponse = await api.get(`/subscriptions/${examId}/status`);
          currentHasSubscription = subResponse.data.hasActiveSubscription;
          setHasSubscription(currentHasSubscription);
          setSubscriptionDetails(subResponse.data.subscription);
          console.log('Subscription status refreshed:', currentHasSubscription);
          
          // Hide upgrade wall if subscription is active or user is admin
          if (currentHasSubscription || isAdmin) {
            setShowUpgradeWall(false);
          }
          
          // Check if exam mode requires premium (for users without subscription and not admin)
          if (selectedMode === 'EXAM' && !currentHasSubscription && !isAdmin) {
            console.log('User without subscription trying exam mode - showing upgrade wall');
            setUpgradeReason('exam_mode');
            setShowUpgradeWall(true);
            return;
          }
        } catch (error) {
          console.log('Error checking subscription:', error);
          // If check fails and trying exam mode, show upgrade wall (unless admin)
          if (selectedMode === 'EXAM' && !isAdmin) {
            setUpgradeReason('exam_mode');
            setShowUpgradeWall(true);
            return;
          }
          // For practice mode or admin, continue anyway (might have free questions or admin access)
        }
      } else if (selectedMode === 'EXAM') {
        // Guest trying exam mode
        console.log('Guest trying exam mode - showing upgrade wall');
        setUpgradeReason('exam_mode');
        setShowUpgradeWall(true);
        return;
      }
    }

    // Determine question limit based on exam configuration
    let finalQuestionLimit = questionLimit;
    
    // If practice mode with exam-like settings enabled, use exam question count
    // But only for subscribers or admins
    if (selectedMode === 'PRACTICE' && exam?.practice_mode_enabled && exam?.exam_question_count) {
      if (hasSubscription || isAdmin) {
        finalQuestionLimit = exam.exam_question_count;
      } else {
        // Non-subscribers and non-admins get 20 questions (or 10 for guests)
        finalQuestionLimit = isGuest ? 10 : 20;
      }
    }
    // If exam mode and exam question count is set, use it
    else if (selectedMode === 'EXAM' && exam?.exam_question_count) {
      finalQuestionLimit = exam.exam_question_count;
    }
    
    // Guests can only do practice mode with max 10 questions
    if (isGuest) {
      finalQuestionLimit = Math.min(finalQuestionLimit, 10);
      setIsGuestMode(true);
    }

    console.log('Question limit calculation:', {
      selectedMode,
      questionLimit,
      finalQuestionLimit,
      practice_mode_enabled: exam?.practice_mode_enabled,
      exam_question_count: exam?.exam_question_count
    });

    try {
      setLoading(true);
      const currentLanguage = i18n.language || 'en';
      const isNonEnglish = currentLanguage !== 'en';
      
      // Fetch questions in current language
      const response = await api.get(`/exams/${examId}/questions`, {
        params: { mode: selectedMode, limit: finalQuestionLimit, language: currentLanguage }
      });
      
      // If non-English language and practice mode, also fetch English version for toggle
      if (isNonEnglish && selectedMode === 'PRACTICE') {
        try {
          const englishResponse = await api.get(`/exams/${examId}/questions`, {
            params: { mode: selectedMode, limit: finalQuestionLimit, language: 'en' }
          });
          setEnglishQuestions(englishResponse.data.questions || []);
        } catch (err) {
          console.error('Failed to fetch English questions:', err);
          setEnglishQuestions([]);
        }
      } else {
        setEnglishQuestions([]);
        setShowEnglish(false);
      }

      // Check if free questions exhausted (only for users without subscription and not admin)
      if (response.data.freeQuestionsExhausted && !currentHasSubscription && !isAdmin) {
        setFreeQuestionsExhausted(true);
        setUpgradeReason('exhausted_free');
        setShowUpgradeWall(true);
        setLoading(false);
        return;
      }

      // Check if no questions available
      if (!response.data.questions || response.data.questions.length === 0) {
        // If user has subscription or is admin, they should have access to questions
        // Only show upgrade wall if they don't have subscription and are not admin
        if (user && (currentHasSubscription || isAdmin)) {
          // User has subscription/admin but no questions - this shouldn't happen, but don't show upgrade wall
          console.warn('User has subscription/admin but no questions returned');
          alert('No questions available for this exam. Please contact support.');
        } else {
          // No subscription or guest - show appropriate upgrade wall (unless admin)
          if (!isAdmin) {
            if (!hasFreeTier) {
              setUpgradeReason('no_free_tier');
            } else if (user) {
              setUpgradeReason('exhausted_free');
            } else {
              setUpgradeReason('no_free_tier');
            }
            setShowUpgradeWall(true);
          }
        }
        setLoading(false);
        return;
      }
      
      // If we got here and user has subscription or is admin, make sure upgrade wall is hidden
      if (currentHasSubscription || isAdmin) {
        setShowUpgradeWall(false);
      }

      setQuestions(response.data.questions);
      setMode(selectedMode);
      setCurrentQuestionIndex(0);
      setAnswers({});
      setFlaggedQuestions(new Set());
      setShowResult(false);
      setResult(null);
      setFeedback({});

      // Set timer based on exam configuration
      if (selectedMode === 'EXAM' && !isGuest) {
        // Use exam duration if configured, otherwise fallback to question count * 60 seconds
        const examDuration = exam?.exam_duration_minutes 
          ? exam.exam_duration_minutes * 60 
          : finalQuestionLimit * 60;
        setTimeRemaining(examDuration);
      } else if (selectedMode === 'PRACTICE' && exam?.practice_mode_enabled && exam?.exam_duration_minutes) {
        // Practice mode with exam-like settings enabled
        setTimeRemaining(exam.exam_duration_minutes * 60);
      } else {
        setTimeRemaining(null);
      }
    } catch (error: any) {
      if (error.response?.status === 403) {
        setUpgradeReason('premium_only');
        setShowUpgradeWall(true);
      } else {
        console.error('Failed to start exam:', error);
        alert('Failed to load questions. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId: number, answer: string) => {
    const question = questions.find((q) => q.id === questionId);
    const correctAnswerKeys = question?.correct_answer_keys || 
      (question?.correct_answer_key ? [question.correct_answer_key] : []);
    
    // Determine if this is a multiple-answer question
    // Check if correct_answer_keys has more than one answer
    const hasMultipleAnswers = correctAnswerKeys.length > 1;
    
    // Calculate the new answer value first
    let newAnswer: string | string[];
    const currentAnswer = answers[questionId];
    
    if (hasMultipleAnswers) {
      // Toggle answer for multiple choice questions
      const currentAnswers = Array.isArray(currentAnswer) ? currentAnswer : (currentAnswer ? [currentAnswer] : []);
      const newAnswers = currentAnswers.includes(answer)
        ? currentAnswers.filter(a => a !== answer)
        : [...currentAnswers, answer].sort();
      
      newAnswer = newAnswers.length > 0 ? newAnswers : [];
      
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('Multiple answer question:', {
          questionId,
          correctAnswerKeys,
          currentAnswers,
          newAnswers,
          answer
        });
      }
    } else {
      // Single answer for single choice questions
      newAnswer = answer;
    }
    
    // Update the answers state
    setAnswers((prev) => ({
      ...prev,
      [questionId]: newAnswer
    }));

    // Calculate feedback in practice mode using the new answer value
    if (mode === 'PRACTICE') {
      if (question) {
        const correctAnswers = question.correct_answer_keys || 
          (question.correct_answer_key ? [question.correct_answer_key] : []);
        const userAnswers = Array.isArray(newAnswer) ? newAnswer : (newAnswer ? [newAnswer] : []);
        
        // For multiple-answer questions, wait until user selects the same number of choices
        // For single-answer questions, show feedback immediately
        const isMultipleAnswer = correctAnswers.length > 1;
        const hasSelectedEnoughAnswers = userAnswers.length === correctAnswers.length;
        
        // Only show feedback if:
        // 1. Single answer question (show immediately), OR
        // 2. Multiple answer question AND user has selected the correct number of answers
        if (!isMultipleAnswer || hasSelectedEnoughAnswers) {
          // Check if all correct answers are selected and no incorrect answers
          const selectedCorrect = correctAnswers.filter(ca => userAnswers.includes(ca));
          const isCorrect = correctAnswers.length === selectedCorrect.length && 
                           userAnswers.length === correctAnswers.length;
          
          setFeedback((prev) => ({
            ...prev,
            [questionId]: {
              correct: isCorrect,
              explanation: question.explanation || ''
            }
          }));
        } else {
          // For multiple-answer questions, clear feedback until user selects enough answers
          setFeedback((prev) => {
            const newFeedback = { ...prev };
            delete newFeedback[questionId];
            return newFeedback;
          });
        }
      }
    }
  };

  const toggleFlag = (questionId: number) => {
    setFlaggedQuestions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleSubmit = async () => {
    if (questions.length === 0) return;

    // For guests, just show results without saving
    if (isGuest) {
      const answerArray = questions.map((q) => ({
        question_id: q.id,
        user_answer: answers[q.id] || ''
      }));

      let correctCount = 0;
      const details = answerArray.map(answer => {
        const question = questions.find(q => q.id === answer.question_id);
        // Handle both old format (singular) and new format (array)
        let correctAnswerKeys = question?.correct_answer_keys || 
          (question?.correct_answer_key ? [question.correct_answer_key] : []);
        
        // Normalize correct answers
        correctAnswerKeys = correctAnswerKeys.map(key => String(key).trim().toUpperCase());
        
        // Normalize user answer
        let userAnswers = Array.isArray(answer.user_answer) 
          ? answer.user_answer 
          : (answer.user_answer ? [answer.user_answer] : []);
        userAnswers = userAnswers.map(ua => String(ua).trim().toUpperCase()).filter(ua => ua.length > 0);
        
        // Check correctness
        let isCorrectFinal = false;
        if (correctAnswerKeys.length === 1) {
          // Single answer question
          isCorrectFinal = userAnswers.length === 1 && userAnswers[0] === correctAnswerKeys[0];
        } else if (correctAnswerKeys.length > 1) {
          // Multiple answer question - all correct answers must be selected, no incorrect ones
          const selectedCorrect = correctAnswerKeys.filter(ca => userAnswers.includes(ca));
          const selectedIncorrect = userAnswers.filter(ua => !correctAnswerKeys.includes(ua));
          isCorrectFinal = selectedIncorrect.length === 0 && 
                          selectedCorrect.length === correctAnswerKeys.length && 
                          userAnswers.length === correctAnswerKeys.length;
        }
        
        if (isCorrectFinal) correctCount++;
        
        return {
          question_id: answer.question_id,
          user_answer: Array.isArray(answer.user_answer) ? answer.user_answer.join(',') : answer.user_answer,
          correct: isCorrectFinal,
          topic: question?.topic || 'Other'
        };
      });

      const totalQuestions = questions.length;
      const scorePercent = totalQuestions > 0 ? (correctCount / totalQuestions) * 100 : 0;

      setResult({
        score: {
          percent: scorePercent,
          correct: correctCount,
          total: totalQuestions
        },
        details: details.map(d => {
          const question = questions.find(q => q.id === d.question_id);
          return {
            ...d,
            id: d.question_id,
            question_id: d.question_id,
            topic: question?.topic || 'Other'
          };
        })
      });
      setShowResult(true);
      return;
    }

    // Helper function to normalize answer keys (extract just the letter)
    const normalizeAnswerKey = (key: string | string[] | undefined): string | string[] => {
      if (!key) return '';
      if (Array.isArray(key)) {
        return key
          .map(k => {
            const str = String(k).trim().toUpperCase();
            const match = str.match(/^([A-Z])/);
            return match ? match[1] : '';
          })
          .filter(k => k.length > 0);
      } else {
        const str = String(key).trim().toUpperCase();
        const match = str.match(/^([A-Z])/);
        return match ? match[1] : '';
      }
    };
    
    // For logged in users, save to database
    const answerArray = questions.map((q) => {
      const answer = answers[q.id];
      // Normalize answer: extract just the letter (e.g., "C" from "C", "C.", "C: HMAC-SHA1", etc.)
      const normalizedAnswer = normalizeAnswerKey(answer);
      
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log(`Question ${q.id} answer normalization:`, {
          raw: answer,
          normalized: normalizedAnswer
        });
      }
      
      return {
        question_id: q.id,
        user_answer: normalizedAnswer
      };
    });

    try {
      // Calculate time taken based on exam configuration
      let timeTaken = null;
      if (timeRemaining !== null) {
        if (mode === 'EXAM' && exam?.exam_duration_minutes) {
          timeTaken = (exam.exam_duration_minutes * 60) - timeRemaining;
        } else if (mode === 'PRACTICE' && exam?.practice_mode_enabled && exam?.exam_duration_minutes) {
          timeTaken = (exam.exam_duration_minutes * 60) - timeRemaining;
        } else if (mode === 'EXAM') {
          // Fallback to question count * 60 if no exam duration configured
          timeTaken = (questions.length * 60) - timeRemaining;
        }
      }

      const response = await api.post(`/exams/${examId}/submit`, {
        mode,
        answers: answerArray,
        timeTakenSeconds: timeTaken
      });

      setResult(response.data);
      setShowResult(true);
    } catch (error) {
      console.error('Failed to submit exam:', error);
      alert('Failed to submit exam. Please try again.');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Always render UpgradeWall if needed, even during loading
  const upgradeWallElement = showUpgradeWall && exam ? (
    <UpgradeWall
      examId={parseInt(examId!)}
      examName={exam?.name || 'Exam'}
      reason={upgradeReason}
      freeModeEnabled={freeModeEnabled}
      onClose={() => {
        console.log('Closing upgrade wall');
        setShowUpgradeWall(false);
      }}
    />
  ) : null;

  if (!exam) {
    return (
      <>
        <SEO title="Loading Exam - GlobalPreps" />
        {upgradeWallElement}
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="gp-spinner mb-3"></div>
            <p className="gp-text-muted">Loading exam...</p>
          </div>
        </div>
      </>
    );
  }

  if (!mode) {
    return (
      <>
        <SEO
          title={exam ? `${exam.name} - Practice & Exam Mode - GlobalPreps` : 'Exam - GlobalPreps'}
          description={exam?.description || `Practice certification exam with instant feedback and detailed explanations. Choose between practice mode or timed exam simulation.`}
          keywords={exam ? `${exam.name}, certification exam, exam practice, ${exam.name} practice test, ${exam.name} exam simulator` : 'certification exam, exam practice'}
          url={exam ? getExamUrl(exam.id, exam.name) : `/exam/${examId}`}
          structuredData={exam ? {
            "@context": "https://schema.org",
            "@type": "Course",
            "name": exam.name,
            "description": exam.description || `${exam.name} certification exam practice`,
            "provider": {
              "@type": "Organization",
              "name": "GlobalPreps"
            },
            "courseCode": exam.name
          } : undefined}
        />
        {upgradeWallElement}
        <div className="gp-page py-6">
          <div className="gp-container">
            {/* Back Button and Edit Button (Admin) */}
            <div dir={rtlDir} className={`flex items-center mb-6 ${isPageRTL ? 'justify-end' : 'justify-between'}`}>
              <Link
                to={user ? "/dashboard" : "/"}
                className={`inline-flex items-center text-[color:var(--gp-brand)] hover:opacity-80 font-medium text-sm transition-colors ${isPageRTL ? 'flex-row-reverse' : ''}`}
              >
                <FontAwesomeIcon icon={faArrowLeft} className={isPageRTL ? 'ml-2' : 'mr-2'} style={{ transform: isPageRTL ? 'scaleX(-1)' : 'none' }} />
                {user ? t('common.backToDashboard') : t('common.backToHome')}
              </Link>
              
              {/* Admin Edit Button */}
              {user?.isAdmin === true && exam && (
                <Link
                  to={`/admin/certifications?edit=${exam.id}`}
                  className={`inline-flex items-center gp-text-muted hover:text-[color:var(--gp-brand)] font-medium text-sm transition-colors ${isPageRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2'}`}
                >
                  <FontAwesomeIcon icon={faEdit} className="text-xs" />
                  <span>{t('common.edit')}</span>
                </Link>
              )}
            </div>

            {/* Header Section */}
            <div className={`mb-5 ${isPageRTL ? 'text-right' : 'text-center'}`} dir={rtlDir}>
              {exam.badge_image ? (
                <img 
                  src={`/${exam.badge_image}`} 
                  alt={`${exam.name} badge`}
                  className="w-32 h-32 object-contain mx-auto mb-3 sm:mb-4"
                  onError={(e) => {
                    // Fallback to icon if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`inline-flex items-center justify-center w-12 h-12 bg-[linear-gradient(135deg,var(--gp-brand),#17838d)] rounded-lg mb-3 sm:mb-4  ${exam.badge_image ? 'hidden' : ''} ${isPageRTL ? 'ml-auto' : 'mx-auto'}`}>
                <FontAwesomeIcon icon={faGraduationCap} className="text-white text-xl sm:text-2xl" />
              </div>
              <h1 className={`text-xl sm:text-2xl font-bold text-[color:var(--gp-ink)] mb-2 px-2 ${isPageRTL ? 'text-right' : 'text-center'}`} dir={rtlDir}>{exam.name}</h1>
              {exam.description && (
                <p className={`text-xs sm:text-sm gp-text-muted max-w-2xl mb-3 px-4 ${isPageRTL ? 'text-right ml-auto' : 'text-center mx-auto'}`} dir={rtlDir}>{exam.description}</p>
              )}
              
              {/* Subscription Status Badge */}
              {user && hasSubscription && subscriptionDetails && (
                <div className={`inline-flex items-center ${isPageRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-300 dark:border-green-700 rounded-lg mt-3`}>
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 dark:text-green-400 text-sm" />
                  <span className="text-sm font-semibold text-green-700 dark:text-green-300">
                    Premium Active
                  </span>
                  <span className="text-xs text-green-600 dark:text-green-400">
                    • Expires {new Date(subscriptionDetails.end_date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              )}
            </div>

            {/* Completed Free Practice Banner for Guests - Shown at top */}
            {isGuest && showResult && (
              <div className={`card bg-[linear-gradient(135deg,var(--gp-ink),var(--gp-brand))] text-white mb-6 relative overflow-hidden`} dir={rtlDir}>
                <div className="absolute inset-0 bg-black/10"></div>
                <div className={`relative z-10 flex flex-col md:flex-row items-center justify-between p-6 ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`mb-4 md:mb-0 flex-1 ${isPageRTL ? 'text-center md:text-right' : 'text-center md:text-left'}`}>
                    <h3 className={`text-lg font-bold mb-2 flex items-center ${isPageRTL ? 'justify-center md:justify-end flex-row-reverse' : 'justify-center md:justify-start'}`}>
                      <FontAwesomeIcon icon={faCrown} className={isPageRTL ? 'ml-2' : 'mr-2'} style={{ color: '#fde047' }} />
                      {t('exam.unlockFullAccess')}
                    </h3>
                    <p className={`text-white/80 text-sm ${isPageRTL ? 'text-right' : 'text-left'}`}>
                      {t('exam.completedFreePractice')}
                    </p>
                  </div>
                  <Link
                    to="/register"
                    state={{ examId: examId }}
                    className={`bg-white text-[color:var(--gp-brand)] hover:bg-white/90 font-semibold py-2.5 px-6 rounded-lg transition-all  hover: flex items-center ${isPageRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'} text-sm`}
                  >
                    <span>{t('auth.signUp')}</span>
                    <FontAwesomeIcon icon={faArrowRight} className="text-xs" style={{ transform: isPageRTL ? 'scaleX(-1)' : 'none' }} />
                  </Link>
                </div>
              </div>
            )}

            {/* Upgrade Banner for Guests - Show when not logged in and haven't completed exam */}
            {isGuest && !showResult && exam && (
              <div className={`card text-white mb-6 relative overflow-hidden ${
                freeModeEnabled 
                  ? 'bg-gradient-to-r from-green-500 via-emerald-600 to-teal-600' 
                  : 'bg-[linear-gradient(135deg,var(--gp-ink),var(--gp-brand))]'
              }`} dir={rtlDir}>
                <div className="absolute inset-0 bg-black/10"></div>
                <div className={`relative z-10 flex flex-col md:flex-row items-center justify-between p-5 ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`mb-4 md:mb-0 flex-1 ${isPageRTL ? 'text-center md:text-right' : 'text-center md:text-left'}`}>
                    <h3 className={`text-base font-bold mb-2 flex items-center ${isPageRTL ? 'justify-center md:justify-end flex-row-reverse' : 'justify-center md:justify-start'}`}>
                      <FontAwesomeIcon icon={freeModeEnabled ? faRocket : faCrown} className={isPageRTL ? 'ml-2' : 'mr-2'} style={{ color: freeModeEnabled ? '#fef9c3' : '#fde047' }} />
                      {freeModeEnabled ? '🎉 Sign Up for Unlimited Free Questions!' : t('exam.try10QuestionsFree')}
                    </h3>
                    <p className={`text-sm ${freeModeEnabled ? 'text-green-50 font-medium' : 'text-white/80'} ${isPageRTL ? 'text-right' : 'text-left'}`}>
                      {freeModeEnabled 
                        ? 'Create a free account to access ALL questions, exam mode, and features - 100% free, no payment required!'
                        : t('exam.signUpToGet20')
                      }
                    </p>
                  </div>
                  <Link
                    to="/register"
                    state={{ examId: examId }}
                    className={`font-semibold py-2.5 px-6 rounded-lg transition-all  hover: flex items-center ${isPageRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'} text-sm ${
                      freeModeEnabled
                        ? 'bg-white text-green-600 hover:bg-green-50'
                        : 'bg-white text-[color:var(--gp-brand)] hover:bg-white/90'
                    }`}
                  >
                    <span>{t('auth.signUp')}</span>
                    <FontAwesomeIcon icon={faArrowRight} className="text-xs" style={{ transform: isPageRTL ? 'scaleX(-1)' : 'none' }} />
                  </Link>
                </div>
              </div>
            )}

            {/* Mode Selection */}
            <div className="mb-5" dir={rtlDir}>
              <div className={`mb-4 sm:mb-6 ${isPageRTL ? 'text-right' : 'text-center'}`}>
                <h2 className={`text-lg sm:text-xl font-bold text-[color:var(--gp-ink)] mb-1 sm:mb-2 ${isPageRTL ? 'text-right' : 'text-center'}`}>
                  {t('exam.choosePracticeMode')}
                </h2>
                <p className={`text-xs sm:text-sm gp-text-muted px-2 ${isPageRTL ? 'text-right' : 'text-center'}`}>
                  {t('exam.selectModeDescription')}
                </p>
              </div>
              
              <div className={`grid ${isGuest ? 'grid-cols-1 max-w-2xl' : 'grid-cols-1 md:grid-cols-2'} gap-3 sm:gap-4 md:gap-6 ${isPageRTL ? 'ml-auto' : 'mx-auto'}`}>
                {/* Practice Mode Card */}
                <div className={`card gp-card-interactive border border-[color:var(--gp-brand)] relative overflow-hidden group p-4`} dir={rtlDir}>
                  <div className={`absolute ${isPageRTL ? 'top-0 left-0' : 'top-0 right-0'} w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-[color:var(--gp-brand-soft)] to-transparent ${isPageRTL ? 'rounded-br-full' : 'rounded-bl-full'}`}></div>
                  <div className="relative z-10">
                    <div className={`flex items-center mb-4 sm:mb-5 ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-10 h-10 bg-[linear-gradient(135deg,var(--gp-brand),#17838d)] rounded-lg sm:rounded-xl flex items-center justify-center ${isPageRTL ? 'ml-3 sm:ml-4' : 'mr-3 sm:mr-4'}  group-hover:scale-110 transition-transform flex-shrink-0`}>
                        <FontAwesomeIcon icon={faBook} className="text-white text-base sm:text-xl" />
                      </div>
                      <div className={`flex-1 min-w-0 ${isPageRTL ? 'text-right' : 'text-left'}`}>
                        <h3 className="text-base sm:text-lg font-bold text-[color:var(--gp-ink)]">{t('exam.practiceMode')}</h3>
                        {isGuest && (
                          <p className="text-xs text-[color:var(--gp-brand)] font-semibold mt-0.5">
                            {t('exam.tenQuestionsFree')}
                          </p>
                        )}
                      </div>
                    </div>
                    <ul className={`space-y-2 sm:space-y-2.5 mb-4 sm:mb-6 ${isPageRTL ? 'text-right' : 'text-left'}`}>
                      <li className={`flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center ${isPageRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} flex-shrink-0 mt-0.5 sm:mt-0`}>
                          <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 dark:text-green-400 text-[10px] sm:text-xs" />
                        </div>
                        <span className="leading-relaxed">{t('exam.instantFeedback')}</span>
                      </li>
                      <li className={`flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[color:var(--gp-brand-soft)] flex items-center justify-center ${isPageRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} flex-shrink-0 mt-0.5 sm:mt-0`}>
                          <FontAwesomeIcon icon={faInfoCircle} className="text-[color:var(--gp-brand)] text-[10px] sm:text-xs" />
                        </div>
                        <span className="leading-relaxed">{t('exam.seeExplanations')}</span>
                      </li>
                      {exam?.practice_mode_enabled && exam?.exam_duration_minutes ? (
                        <li className={`flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center ${isPageRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} flex-shrink-0 mt-0.5 sm:mt-0`}>
                            <FontAwesomeIcon icon={faClock} className="text-red-600 dark:text-red-400 text-[10px] sm:text-xs" />
                          </div>
                          <span className="leading-relaxed">
                            {t('exam.examLikeTimer', { minutes: exam.exam_duration_minutes })}
                          </span>
                        </li>
                      ) : (
                        <li className={`flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[color:var(--gp-surface-muted)] flex items-center justify-center ${isPageRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} flex-shrink-0 mt-0.5 sm:mt-0`}>
                            <FontAwesomeIcon icon={faClock} className="gp-text-muted text-[10px] sm:text-xs" />
                          </div>
                          <span className="leading-relaxed">{t('exam.noTimeLimit')}</span>
                        </li>
                      )}
                      {exam?.practice_mode_enabled && exam?.exam_question_count && (hasSubscription || user?.isAdmin === true) && (
                        <li className={`flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[color:var(--gp-accent-soft)] flex items-center justify-center ${isPageRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} flex-shrink-0 mt-0.5 sm:mt-0`}>
                            <FontAwesomeIcon icon={faQuestionCircle} className="text-[color:var(--gp-accent)] text-[10px] sm:text-xs" />
                          </div>
                          <span className="leading-relaxed">
                            {t('exam.questionsMatchesExam', { count: exam.exam_question_count })}
                          </span>
                        </li>
                      )}
                      {!isGuest && (!hasSubscription && user?.isAdmin !== true) && (
                        <li className={`flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[color:var(--gp-accent-soft)] flex items-center justify-center ${isPageRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} flex-shrink-0 mt-0.5 sm:mt-0`}>
                            <FontAwesomeIcon icon={faQuestionCircle} className="text-[color:var(--gp-accent)] text-[10px] sm:text-xs" />
                          </div>
                          <span className="leading-relaxed">
                            {t('exam.twentyQuestions')}
                          </span>
                        </li>
                      )}
                      <li className={`flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center ${isPageRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} flex-shrink-0 mt-0.5 sm:mt-0`}>
                          <FontAwesomeIcon icon={faLightbulb} className="text-yellow-600 dark:text-yellow-400 text-[10px] sm:text-xs" />
                        </div>
                        <span className="leading-relaxed">{t('exam.perfectForLearning')}</span>
                      </li>
                    </ul>
                    <button 
                      onClick={() => {
                        const isAdmin = user?.isAdmin === true;
                        const canUseExamCount = (hasSubscription || isAdmin) && exam?.practice_mode_enabled && exam?.exam_question_count;
                        const questionLimit = canUseExamCount ? exam.exam_question_count : (isGuest ? 10 : 20);
                        startExam('PRACTICE', questionLimit);
                      }}
                      className={`w-full gp-btn-primary transition-all flex items-center justify-center ${isPageRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'}`}
                    >
                      <span>
                        {(() => {
                          const isAdmin = user?.isAdmin === true;
                          const canUseExamCount = (hasSubscription || isAdmin) && exam?.practice_mode_enabled && exam?.exam_question_count;
                          if (canUseExamCount) {
                            return t('exam.startPracticeWithCount', { count: exam.exam_question_count });
                          } else if (isGuest) {
                            return t('exam.startPractice10');
                          } else {
                            return t('exam.startPractice20');
                          }
                        })()}
                      </span>
                      <FontAwesomeIcon icon={faArrowRight} className="text-xs" style={{ transform: isPageRTL ? 'scaleX(-1)' : 'none' }} />
                    </button>
                  </div>
                </div>

                {/* Exam Mode Card - For Logged In Users */}
                {!isGuest && (
                  <div className={`card gp-card-interactive border border-[color:var(--gp-accent)] relative overflow-hidden group p-4`} dir={rtlDir}>
                    <div className={`absolute ${isPageRTL ? 'top-0 left-0' : 'top-0 right-0'} w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br from-[color:var(--gp-accent-soft)] to-transparent ${isPageRTL ? 'rounded-br-full' : 'rounded-bl-full'}`}></div>
                    <div className="relative z-10">
                      <div className={`flex items-center mb-4 sm:mb-5 ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`w-10 h-10 bg-[linear-gradient(135deg,var(--gp-accent),#a34a1c)] rounded-lg sm:rounded-xl flex items-center justify-center ${isPageRTL ? 'ml-3 sm:ml-4' : 'mr-3 sm:mr-4'}  group-hover:scale-110 transition-transform flex-shrink-0`}>
                          <FontAwesomeIcon icon={faGraduationCap} className="text-white text-base sm:text-xl" />
                        </div>
                        <div className={`flex-1 min-w-0 ${isPageRTL ? 'text-right' : 'text-left'}`}>
                          <h3 className="text-base sm:text-lg font-bold text-[color:var(--gp-ink)]">{t('exam.examMode')}</h3>
                          <p className="text-xs text-[color:var(--gp-accent)] font-semibold mt-0.5">
                            {t('exam.premiumFeature')}
                          </p>
                        </div>
                      </div>
                      <ul className={`space-y-2 sm:space-y-2.5 mb-4 sm:mb-6 ${isPageRTL ? 'text-right' : 'text-left'}`}>
                        <li className={`flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[color:var(--gp-accent-soft)] flex items-center justify-center ${isPageRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} flex-shrink-0 mt-0.5 sm:mt-0`}>
                            <FontAwesomeIcon icon={faGraduationCap} className="text-[color:var(--gp-accent)] text-[10px] sm:text-xs" />
                          </div>
                          <span className="leading-relaxed">{t('exam.simulatedExamConditions')}</span>
                        </li>
                        <li className={`flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center ${isPageRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} flex-shrink-0 mt-0.5 sm:mt-0`}>
                            <FontAwesomeIcon icon={faClock} className="text-red-600 dark:text-red-400 text-[10px] sm:text-xs" />
                          </div>
                          <span className="leading-relaxed">{t('exam.timerCountdown')}</span>
                        </li>
                        <li className={`flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center ${isPageRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} flex-shrink-0 mt-0.5 sm:mt-0`}>
                            <FontAwesomeIcon icon={faTrophy} className="text-yellow-600 dark:text-yellow-400 text-[10px] sm:text-xs" />
                          </div>
                          <span className="leading-relaxed">{t('exam.reviewResultsAfter')}</span>
                        </li>
                        <li className={`flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center ${isPageRTL ? 'ml-2 sm:ml-3' : 'mr-2 sm:mr-3'} flex-shrink-0 mt-0.5 sm:mt-0`}>
                            <FontAwesomeIcon icon={faFlag} className="text-orange-600 dark:text-orange-400 text-[10px] sm:text-xs" />
                          </div>
                          <span className="leading-relaxed">{t('exam.flagQuestionsForReview')}</span>
                        </li>
                      </ul>
                      <button 
                        onClick={() => startExam('EXAM', exam?.exam_question_count || 20)}
                        className={`w-full gp-btn-accent transition-all flex items-center justify-center ${isPageRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'}`}
                      >
                        <span>{exam?.exam_question_count ? t('exam.startExamWithCount', { count: exam.exam_question_count }) : t('exam.startExam')}</span>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" style={{ transform: isPageRTL ? 'scaleX(-1)' : 'none' }} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Exam Mode Card - Locked for Guests (Hide in free mode) */}
                {isGuest && !freeModeEnabled && (
                  <div className="card border border-[color:var(--gp-accent)] relative overflow-hidden opacity-60 p-4">
                    <div className="absolute inset-0 bg-[color:var(--gp-surface)]/90 backdrop-blur-sm rounded-xl flex items-center justify-center z-20">
                      <div className="text-center p-4">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-[linear-gradient(135deg,var(--gp-accent),#a34a1c)] flex items-center justify-center mx-auto mb-3 sm:mb-4 ">
                          <FontAwesomeIcon icon={faLock} className="text-white text-lg sm:text-2xl" />
                        </div>
                        <h4 className="text-sm sm:text-base font-bold text-[color:var(--gp-ink)] mb-1 sm:mb-2">{t('exam.subscribeToUnlockMore')}</h4>
                        <p className="text-xs sm:text-sm gp-text-muted mb-3 sm:mb-4 px-2">
                          {t('exam.signUpForExamMode')}
                        </p>
                        <Link
                          to="/register"
                          state={{ examId: examId }}
                          className="inline-flex items-center space-x-2 px-3 sm:px-4 py-2  text-white rounded-lg transition-all shadow-md hover: text-xs sm:text-sm font-semibold"
                        >
                          <span>{t('auth.signUp')}</span>
                          <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        </Link>
                      </div>
                    </div>
                    <div className="relative z-10 opacity-50">
                      <div className="flex items-center mb-4 sm:mb-5">
                        <div className="w-10 h-10 bg-[linear-gradient(135deg,var(--gp-accent),#a34a1c)] rounded-lg sm:rounded-xl flex items-center justify-center mr-3 sm:mr-4  flex-shrink-0">
                          <FontAwesomeIcon icon={faGraduationCap} className="text-white text-base sm:text-xl" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg font-bold text-[color:var(--gp-ink)]">{t('exam.examMode')}</h3>
                          <p className="text-xs text-[color:var(--gp-accent)] font-semibold mt-0.5">
                            {t('exam.premiumFeature')}
                          </p>
                        </div>
                      </div>
                      <ul className="space-y-2 sm:space-y-2.5 mb-4 sm:mb-6">
                        <li className="flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)]">
                          <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[color:var(--gp-accent-soft)] flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0 mt-0.5 sm:mt-0">
                            <FontAwesomeIcon icon={faGraduationCap} className="text-[color:var(--gp-accent)] text-[10px] sm:text-xs" />
                          </div>
                          <span className="leading-relaxed">{t('exam.simulatedExamConditions')}</span>
                        </li>
                        <li className="flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)]">
                          <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0 mt-0.5 sm:mt-0">
                            <FontAwesomeIcon icon={faClock} className="text-red-600 dark:text-red-400 text-[10px] sm:text-xs" />
                          </div>
                          <span className="leading-relaxed">{t('exam.timerCountdown')}</span>
                        </li>
                        <li className="flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)]">
                          <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0 mt-0.5 sm:mt-0">
                            <FontAwesomeIcon icon={faTrophy} className="text-yellow-600 dark:text-yellow-400 text-[10px] sm:text-xs" />
                          </div>
                          <span className="leading-relaxed">{t('exam.reviewResultsAfter')}</span>
                        </li>
                        <li className="flex items-start sm:items-center text-xs sm:text-sm text-[color:var(--gp-ink-muted)]">
                          <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0 mt-0.5 sm:mt-0">
                            <FontAwesomeIcon icon={faFlag} className="text-orange-600 dark:text-orange-400 text-[10px] sm:text-xs" />
                          </div>
                          <span className="leading-relaxed">{t('exam.flagQuestionsForReview')}</span>
                        </li>
                      </ul>
                      <button 
                        disabled
                        className="w-full gp-btn-accent opacity-50 cursor-not-allowed"
                      >
                        Start Exam
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Exam Details Section */}
              {exam?.exam_details && (
                <div className={`card mt-6 border-2 border-[color:var(--gp-border)]`} dir={rtlDir}>
                  <div className={`flex items-start mb-4 ${isPageRTL ? 'space-x-reverse space-x-3 flex-row-reverse' : 'space-x-3'}`}>
                    <div className="w-10 h-10 rounded-lg bg-[linear-gradient(135deg,var(--gp-brand),var(--gp-accent))] flex items-center justify-center shadow-md flex-shrink-0">
                      <FontAwesomeIcon icon={faInfoCircle} className="text-white text-lg" />
                    </div>
                    <div className={`flex-1 ${isPageRTL ? 'text-right' : 'text-left'}`}>
                      <h3 className={`text-base font-bold text-[color:var(--gp-ink)] mb-2 ${isPageRTL ? 'text-right' : 'text-left'}`}>
                        {t('exam.examDetails') || 'Exam Details'}
                      </h3>
                      <div className={`prose prose-sm dark:prose-invert max-w-none text-sm ${isPageRTL ? 'text-right' : 'text-left'}`} dir={rtlDir}>
                        <MarkdownRenderer content={exam.exam_details} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Recent Activity Section */}
            {user && recentActivity.length > 0 && (
              <div className="card mb-5">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mr-3 shadow-md">
                    <FontAwesomeIcon icon={faHistory} className="text-white text-sm" />
                  </div>
                  <h2 className="text-lg font-bold text-[color:var(--gp-ink)]">Recent Activity</h2>
                </div>
                <div className="space-y-3">
                  {recentActivity.map((activity: any) => {
                    const scorePercent = parseFloat(activity.score_percent || 0);
                    const scoreColor = scorePercent >= 80 ? 'green' : scorePercent >= 60 ? 'yellow' : 'red';
                    const modeLabel = activity.mode === 'EXAM' ? 'Exam' : 'Practice';
                    const timeTaken = activity.time_taken_seconds 
                      ? `${Math.floor(activity.time_taken_seconds / 60)}m ${activity.time_taken_seconds % 60}s`
                      : null;
                    
                    return (
                      <div 
                        key={activity.id}
                        className="flex items-center justify-between p-3 bg-[color:var(--gp-surface-muted)] rounded-lg border border-[color:var(--gp-border)] hover:opacity-90 transition-colors"
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${
                            activity.mode === 'EXAM'
                              ? 'bg-[linear-gradient(135deg,var(--gp-accent),#a34a1c)] text-white'
                              : 'bg-[linear-gradient(135deg,var(--gp-brand),#17838d)] text-white'
                          }`}>
                            {activity.mode === 'EXAM' ? (
                              <FontAwesomeIcon icon={faGraduationCap} className="text-xs" />
                            ) : (
                              <FontAwesomeIcon icon={faBook} className="text-xs" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-semibold text-[color:var(--gp-ink)]">
                                {modeLabel} Session
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                scoreColor === 'green'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : scoreColor === 'yellow'
                                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              }`}>
                                {scorePercent.toFixed(0)}%
                              </span>
                            </div>
                            <div className="flex items-center space-x-3 text-xs gp-text-muted">
                              <span>{activity.correct_answers}/{activity.total_questions} correct</span>
                              {timeTaken && (
                                <>
                                  <span>•</span>
                                  <span className="flex items-center space-x-1">
                                    <FontAwesomeIcon icon={faClock} className="text-xs" />
                                    <span>{timeTaken}</span>
                                  </span>
                                </>
                              )}
                              <span>•</span>
                              <span>{new Date(activity.completed_at).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}</span>
                            </div>
                          </div>
                        </div>
                        <div className="ml-4">
                          {scorePercent >= 80 && (
                            <FontAwesomeIcon icon={faTrophy} className="text-yellow-500 text-lg" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        {upgradeWallElement}
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="gp-spinner mb-3"></div>
            <p className="gp-text-muted">Loading questions...</p>
          </div>
        </div>
      </>
    );
  }

  if (showResult && result) {
    // Debug logging for result
    if (process.env.NODE_ENV === 'development') {
      console.log('Result received:', {
        score: result.score,
        detailsCount: result.details?.length,
        detailsCorrectCount: result.details?.filter((d: any) => d.correct).length,
        detailsIncorrectCount: result.details?.filter((d: any) => d.correct === false).length,
        detailsUndefinedCount: result.details?.filter((d: any) => d.correct === undefined).length,
        sampleDetail: result.details?.[0]
      });
    }
    
    // Calculate topic breakdown
    const topicStats: { [key: string]: { correct: number; total: number } } = {};
    result.details.forEach((detail: any) => {
      const topic = detail.topic || 'Other';
      if (!topicStats[topic]) {
        topicStats[topic] = { correct: 0, total: 0 };
      }
      topicStats[topic].total++;
      if (detail.correct === true) {
        topicStats[topic].correct++;
      }
    });

    const scorePercent = result.score.percent;
    const scorePoints = result.score.points || Math.round((scorePercent / 100) * (result.score.max_points || 1000));
    const maxPoints = result.score.max_points || 1000;
    const passingScore = result.passing_score || exam?.passing_score;
    const passed = result.score.passed !== undefined ? result.score.passed : (passingScore ? scorePoints >= passingScore : null);
    
    const getScoreColor = () => {
      if (passed === true) return 'from-green-500 to-emerald-600';
      if (passed === false) return 'from-red-500 to-red-700';
      if (scorePercent >= 80) return 'from-green-500 to-emerald-600';
      if (scorePercent >= 60) return 'from-yellow-500 to-orange-500';
      return 'from-red-500 to-red-700';
    };

    const getScoreMessage = () => {
      if (passed === true) return { text: t('exam.passedMessage'), icon: faTrophy, color: 'text-yellow-500 dark:text-yellow-400' };
      if (passed === false) return { text: t('exam.notPassed'), icon: faTimesCircle, color: 'text-red-500 dark:text-red-400' };
      if (scorePercent >= 90) return { text: t('exam.excellent'), icon: faTrophy, color: 'text-yellow-500 dark:text-yellow-400' };
      if (scorePercent >= 80) return { text: t('exam.greatJob'), icon: faAward, color: 'text-green-500 dark:text-green-400' };
      if (scorePercent >= 60) return { text: t('exam.goodEffort'), icon: faChartLine, color: 'text-[color:var(--gp-brand)]' };
      return { text: t('exam.keepPracticing'), icon: faLightbulb, color: 'text-orange-500 dark:text-orange-400' };
    };

    const scoreMessage = getScoreMessage();
    const incorrectCount = result.score.total - result.score.correct;
    const timeTaken = result.timeTakenSeconds ? Math.floor(result.timeTakenSeconds / 60) : null;

    return (
      <div className="gp-page py-6">
        <div className="gp-container">
          {/* Header Section */}
          <div className="text-center mb-5">
            <div className="flex items-center justify-center mb-2">
              <FontAwesomeIcon icon={faGraduationCap} className="text-2xl text-[color:var(--gp-brand)] mr-3" />
              <h1 className="text-2xl font-bold text-[color:var(--gp-ink)]">
                {mode === 'EXAM' ? t('exam.examResults') : t('exam.practiceResults')}
              </h1>
            </div>
            <p className="text-sm gp-text-muted">{exam?.name}</p>
          </div>

          {/* Score Card - Hero Section */}
          <div className={`card mb-6 bg-gradient-to-br ${getScoreColor()} text-white relative overflow-hidden`} dir={rtlDir}>
            <div className="absolute inset-0 bg-black/10 dark:bg-black/20"></div>
            <div className="relative z-10">
              <div className={`flex flex-col md:flex-row items-center justify-between ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex-1 mb-6 md:mb-0 ${isPageRTL ? 'text-center md:text-right' : 'text-center md:text-left'}`}>
                  <div className={`flex items-center mb-2 ${isPageRTL ? 'justify-center md:justify-end flex-row-reverse' : 'justify-center md:justify-start'}`}>
                    <FontAwesomeIcon icon={scoreMessage.icon} className={`text-2xl ${scoreMessage.color} ${isPageRTL ? 'ml-2' : 'mr-2'}`} />
                    <h2 className="text-xl font-bold">{scoreMessage.text}</h2>
                  </div>
                  <p className={`text-white/90 text-sm mb-4 ${isPageRTL ? 'text-right' : 'text-left'}`} dangerouslySetInnerHTML={{
                    __html: t('exam.youAnsweredCorrectly', { correct: result.score.correct, total: result.score.total })
                  }}></p>
                  {passingScore && (
                    <div className={`mt-2 text-sm opacity-90 ${isPageRTL ? 'text-right' : 'text-left'}`}>
                      <span dangerouslySetInnerHTML={{
                        __html: t('exam.scorePoints', { points: scorePoints, maxPoints })
                      }}></span>
                      {passed !== null && (
                        <span className={`${isPageRTL ? 'mr-2' : 'ml-2'} font-semibold ${passed ? 'text-green-200' : 'text-red-200'}`}>
                          ({passed ? t('exam.passedUpper') : t('exam.notPassedUpper')} - {t('exam.required', { score: passingScore })})
                        </span>
                      )}
                    </div>
                  )}
                  {timeTaken !== null && (
                    <div className={`flex items-center text-white/90 text-xs ${isPageRTL ? 'justify-center md:justify-end flex-row-reverse' : 'justify-center md:justify-start'}`}>
                      <FontAwesomeIcon icon={faStopwatch} className={isPageRTL ? 'ml-1.5' : 'mr-1.5'} />
                      <span>{t('exam.timeTaken', { minutes: timeTaken })}</span>
                    </div>
                  )}
                </div>
                <div className="relative w-40 h-40 md:w-48 md:h-48">
                  <svg className="transform -rotate-90 w-full h-full">
                    <circle
                      cx="50%"
                      cy="50%"
                      r="45%"
                      stroke="rgba(255,255,255,0.3)"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="50%"
                      cy="50%"
                      r="45%"
                      stroke="white"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${(scorePercent / 100) * 283} 283`}
                      strokeLinecap="round"
                      className="transition-all duration-1000 ease-out"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl md:text-5xl font-bold">
                      {scorePercent.toFixed(0)}%
                    </span>
                    <span className="text-xs text-white/90 mt-1">{t('exam.score')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Grid */}
          <div className={`grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6`} dir={rtlDir}>
            <div className={`card ${isPageRTL ? 'text-right' : 'text-center'} bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800`}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-green-600 dark:text-green-400 mb-2" />
              <div className="text-2xl font-bold text-green-700 dark:text-green-300">{result.score.correct}</div>
              <div className="text-xs text-green-600 dark:text-green-400 font-medium">{t('exam.correct')}</div>
            </div>
            <div className={`card ${isPageRTL ? 'text-right' : 'text-center'} bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800`}>
              <FontAwesomeIcon icon={faTimesCircle} className="text-2xl text-red-600 dark:text-red-400 mb-2" />
              <div className="text-2xl font-bold text-red-700 dark:text-red-300">{incorrectCount}</div>
              <div className="text-xs text-red-600 dark:text-red-400 font-medium">{t('exam.incorrect')}</div>
            </div>
            <div className={`card ${isPageRTL ? 'text-right' : 'text-center'} bg-[color:var(--gp-brand-soft)] border-[color:var(--gp-border)]`}>
              <FontAwesomeIcon icon={faChartBar} className="text-2xl text-[color:var(--gp-brand)] mb-2" />
              <div className="text-2xl font-bold text-[color:var(--gp-brand)]">{result.score.total}</div>
              <div className="text-xs text-[color:var(--gp-brand)] font-medium">{t('exam.total')}</div>
            </div>
            <div className={`card ${isPageRTL ? 'text-right' : 'text-center'} bg-[color:var(--gp-accent-soft)] border-[color:var(--gp-border)]`}>
              <FontAwesomeIcon icon={faAward} className="text-2xl text-[color:var(--gp-accent)] mb-2" />
              <div className="text-2xl font-bold text-[color:var(--gp-accent)]">
                {passed !== null ? (passed ? t('exam.pass') : t('exam.fail')) : (scorePercent >= 80 ? t('exam.pass') : t('exam.fail'))}
              </div>
              <div className="text-xs text-[color:var(--gp-accent)] font-medium">{t('exam.status')}</div>
            </div>
          </div>

          {/* Topic Performance Breakdown */}
          {Object.keys(topicStats).length > 0 && (
            <div className={`card mb-6`} dir={rtlDir}>
              <div className={`flex items-center mb-4 ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                <FontAwesomeIcon icon={faChartLine} className={`text-[color:var(--gp-brand)] ${isPageRTL ? 'ml-2' : 'mr-2'}`} />
                <h2 className={`text-lg font-bold text-[color:var(--gp-ink)] ${isPageRTL ? 'text-right' : 'text-left'}`}>{t('exam.performanceByTopic')}</h2>
              </div>
              <div className="space-y-3">
                {Object.entries(topicStats)
                  .sort((a, b) => {
                    const aPercent = (a[1].correct / a[1].total) * 100;
                    const bPercent = (b[1].correct / b[1].total) * 100;
                    return bPercent - aPercent;
                  })
                  .map(([topic, stats]) => {
                    const topicPercent = (stats.correct / stats.total) * 100;
                    return (
                      <div key={topic} className="space-y-1.5" dir={rtlDir}>
                        <div className={`flex items-center justify-between text-xs mb-1 ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                          <span className={`font-medium text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'text-right' : 'text-left'}`}>{topic}</span>
                          <span className={`gp-text-muted ${isPageRTL ? 'text-right' : 'text-left'}`}>
                            {stats.correct}/{stats.total} ({topicPercent.toFixed(0)}%)
                          </span>
                        </div>
                        <div className="w-full bg-[color:var(--gp-surface-muted)] rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-500 ${
                              topicPercent >= 80
                                ? 'bg-green-500'
                                : topicPercent >= 60
                                ? 'bg-yellow-500'
                                : 'bg-red-500'
                            }`}
                            style={{ width: `${topicPercent}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Question Review Section */}
          <div id="question-review-section" className={`card mb-6`} dir={rtlDir}>
            <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-4 ${isPageRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                <FontAwesomeIcon icon={faBook} className={`text-[color:var(--gp-brand)] ${isPageRTL ? 'ml-2' : 'mr-2'}`} />
                <h2 className={`text-lg font-bold text-[color:var(--gp-ink)] ${isPageRTL ? 'text-right' : 'text-left'}`}>{t('exam.questionReview')}</h2>
              </div>
              
              <div className={`flex items-center gap-4 flex-wrap ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                <span className={`text-xs gp-text-muted ${isPageRTL ? 'text-right' : 'text-left'}`}>
                  {t('exam.correctIncorrect', { correct: result.score.correct, incorrect: incorrectCount })}
                </span>
                
                {/* Filter Buttons */}
                <div className={`flex items-center ${isPageRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} bg-[color:var(--gp-surface-muted)] rounded-lg p-1`}>
                  <button
                    onClick={() => {
                      setAnswerFilter('all');
                      setResultQuestionIndex(0);
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      answerFilter === 'all'
                        ? 'bg-[color:var(--gp-surface)] text-[color:var(--gp-brand)] shadow-sm'
                        : 'gp-text-muted hover:text-[color:var(--gp-ink)]'
                    }`}
                  >
                    {t('exam.allQuestions')} ({result.details.length})
                  </button>
                  <button
                    onClick={() => {
                      setAnswerFilter('correct');
                      setResultQuestionIndex(0);
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center ${
                      answerFilter === 'correct'
                        ? 'bg-[color:var(--gp-surface)] text-green-600 dark:text-green-400 shadow-sm'
                        : 'gp-text-muted hover:text-[color:var(--gp-ink)]'
                    }`}
                  >
                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1.5 text-xs" />
                    {t('exam.correct')} ({result.details.filter((d: any) => d.correct).length})
                  </button>
                  <button
                    onClick={() => {
                      setAnswerFilter('incorrect');
                      setResultQuestionIndex(0);
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center ${
                      answerFilter === 'incorrect'
                        ? 'bg-[color:var(--gp-surface)] text-red-600 dark:text-red-400 shadow-sm'
                        : 'gp-text-muted hover:text-[color:var(--gp-ink)]'
                    }`}
                  >
                    <FontAwesomeIcon icon={faTimesCircle} className="mr-1.5 text-xs" />
                    {t('exam.incorrect')} ({result.details.filter((d: any) => d.correct === false).length})
                  </button>
                </div>
              </div>
            </div>
            
            {/* Copy Protection - Disable text selection and right-click */}
            <div 
              className="space-y-4 select-none"
              onContextMenu={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            >
              {(() => {
                const filteredDetails = result.details.filter((detail: any) => {
                  if (answerFilter === 'all') return true;
                  if (answerFilter === 'correct') return detail.correct === true;
                  if (answerFilter === 'incorrect') return detail.correct === false;
                  return true;
                });
                
                // Show only one question at a time based on resultQuestionIndex
                // Ensure index is within bounds
                const safeIndex = Math.min(resultQuestionIndex, Math.max(0, filteredDetails.length - 1));
                const detail = filteredDetails[safeIndex];
                if (!detail) return null;
                
                // Find the original index in the full details array for proper numbering
                const originalIndex = result.details.findIndex((d: any) => 
                  (d.question_id && detail.question_id && d.question_id === detail.question_id) ||
                  (d.id && detail.id && d.id === detail.id) ||
                  (d === detail)
                );
                const displayIndex = originalIndex >= 0 ? originalIndex : resultQuestionIndex;
                // In exam mode, detail contains full question data; in practice mode, we need to look it up
                const questionId = detail.question_id || detail.id;
                const question = detail.question_text ? detail : questions.find((q) => q.id === questionId);
                
                // Parse correct_answer_keys if it's a string (JSONB)
                let correctAnswerKeys = detail.correct_answer_keys || question?.correct_answer_keys || [];
                if (typeof correctAnswerKeys === 'string') {
                  try {
                    correctAnswerKeys = JSON.parse(correctAnswerKeys);
                  } catch (e) {
                    correctAnswerKeys = [];
                  }
                }
                if (!Array.isArray(correctAnswerKeys)) {
                  correctAnswerKeys = question?.correct_answer_key ? [question.correct_answer_key] : [];
                }
                
                // Get user answer - check detail.userAnswer (exam mode) or detail.user_answer (practice mode)
                const userAnswer = detail.userAnswer || detail.user_answer;
                const userAnswerText = question && userAnswer 
                  ? (Array.isArray(userAnswer) 
                      ? userAnswer.map((key: string) => question.options[key] || key).join(', ')
                      : (question.options[userAnswer] || userAnswer))
                  : 'Not answered';
                
                const correctAnswerTexts = correctAnswerKeys.map((key: string) => {
                  return question?.options[key] || key;
                }).join(', ');
                
                const correctAnswerText = correctAnswerTexts || 'N/A';

                return (
                  <div
                    key={detail.question_id || detail.id || resultQuestionIndex}
                    className={`rounded-lg border-2 transition-all select-none ${
                      detail.correct
                        ? 'bg-green-50 dark:bg-green-900/10 border-green-300 dark:border-green-700'
                        : 'bg-red-50 dark:bg-red-900/10 border-red-300 dark:border-red-700'
                    }`}
                    onContextMenu={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${
                            detail.correct
                              ? 'bg-green-500 text-white'
                              : 'bg-red-500 text-white'
                          }`}>
                            {displayIndex + 1}
                          </div>
                          <div>
                            <div className="font-semibold text-sm text-[color:var(--gp-ink)]">
                              {t('exam.question')} {displayIndex + 1}
                            </div>
                            {detail.topic && (
                              <div className="text-xs gp-text-muted mt-0.5">
                                {detail.topic}
                              </div>
                            )}
                          </div>
                        </div>
                        <span
                          className={`px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center space-x-1 ${
                            detail.correct
                              ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                              : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                          }`}
                        >
                          {detail.correct ? (
                            <>
                              <FontAwesomeIcon icon={faCheckCircle} className="text-xs" />
                              <span>{t('exam.correct')}</span>
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faTimesCircle} className="text-xs" />
                              <span>{t('exam.incorrect')}</span>
                            </>
                          )}
                        </span>
                      </div>
                      
                      {question && (
                        <>
                          <div 
                            className="text-sm text-[color:var(--gp-ink)] mb-4 leading-relaxed font-medium select-none"
                            onContextMenu={(e) => e.preventDefault()}
                            onCopy={(e) => e.preventDefault()}
                            onCut={(e) => e.preventDefault()}
                            onDragStart={(e) => e.preventDefault()}
                          >
                            <MarkdownRenderer content={question.question_text || detail.question_text} />
                          </div>
                          
                          <div 
                            className="space-y-2 bg-[color:var(--gp-surface)] rounded-lg p-3 border border-[color:var(--gp-border)] select-none"
                            onContextMenu={(e) => e.preventDefault()}
                            onCopy={(e) => e.preventDefault()}
                            onCut={(e) => e.preventDefault()}
                            onDragStart={(e) => e.preventDefault()}
                          >
                            <div className={`p-2.5 rounded-lg border ${
                              detail.correct
                                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                            }`}>
                              <div className="flex items-center space-x-2 mb-1">
                                <span className={`text-xs font-semibold ${
                                  detail.correct ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                                }`}>
                                  {detail.correct ? '✓' : '✗'} {t('exam.yourAnswerLabel')}
                                </span>
                              </div>
                              <div className={`text-sm font-medium ${
                                detail.correct 
                                  ? 'text-green-900 dark:text-green-100' 
                                  : 'text-red-900 dark:text-red-100'
                              }`}>
                                {userAnswer ? (
                                  <>
                                    {Array.isArray(userAnswer) ? userAnswer.join(', ') : userAnswer}. <MarkdownRenderer content={userAnswerText} />
                                  </>
                                ) : (
                                  t('exam.notAnswered')
                                )}
                              </div>
                            </div>
                            
                            {/* Show correct answers in exam mode for all questions, or in practice mode for incorrect questions */}
                            {(mode === 'EXAM' || !detail.correct) && correctAnswerKeys.length > 0 && (
                              <div className="p-2.5 rounded-lg border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="text-xs font-semibold text-green-700 dark:text-green-300">
                                    ✓ {correctAnswerKeys.length > 1 ? t('exam.correctAnswers') : t('exam.correctAnswerResult')}:
                                  </span>
                                </div>
                              <div className="text-sm font-medium text-green-900 dark:text-green-100">
                                {correctAnswerKeys.map((key: string, idx: number) => (
                                  <span key={idx}>
                                    {idx > 0 && ', '}
                                    {key}. <MarkdownRenderer content={question?.options?.[key] || detail.options?.[key] || key} />
                                  </span>
                                ))}
                              </div>
                              </div>
                            )}
                            
                            {question.explanation && (
                              <div 
                                className="mt-3 pt-3 border-t border-[color:var(--gp-border)] select-none"
                                onContextMenu={(e) => e.preventDefault()}
                                onCopy={(e) => e.preventDefault()}
                                onCut={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                              >
                                <div className="flex items-start space-x-2">
                                  <FontAwesomeIcon icon={faLightbulb} className="text-yellow-500 dark:text-yellow-400 mt-0.5 flex-shrink-0 text-sm" />
                                  <div className="flex-1">
                                    <strong className="text-xs text-[color:var(--gp-ink-muted)] block mb-1.5 uppercase tracking-wide">
                                      {t('exam.explanationLabel')}
                                    </strong>
                                    <div className="text-sm text-[color:var(--gp-ink-muted)] leading-relaxed">
                                      <MarkdownRenderer content={question.explanation} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
              
              {/* Navigation Controls */}
              {(() => {
                const filteredDetails = result.details.filter((detail: any) => {
                  if (answerFilter === 'all') return true;
                  if (answerFilter === 'correct') return detail.correct === true;
                  if (answerFilter === 'incorrect') return detail.correct === false;
                  return true;
                });
                
                const totalFiltered = filteredDetails.length;
                const canGoPrevious = resultQuestionIndex > 0;
                const canGoNext = resultQuestionIndex < totalFiltered - 1;
                
                return (
                  <div className={`flex items-center justify-between mt-6 pt-6 border-t border-[color:var(--gp-border)] ${isPageRTL ? 'flex-row-reverse' : ''}`} dir={rtlDir}>
                    <button
                      onClick={() => {
                        if (canGoPrevious) {
                          setResultQuestionIndex(resultQuestionIndex - 1);
                          // Scroll to question review section
                          setTimeout(() => {
                            const element = document.getElementById('question-review-section');
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 100);
                        }
                      }}
                      disabled={!canGoPrevious}
                      className={`flex items-center ${isPageRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'} px-4 py-2 rounded-lg font-medium transition-all ${
                        canGoPrevious
                          ? 'bg-[color:var(--gp-brand)] hover:bg-[color:var(--gp-brand-hover)] text-white shadow-md hover:'
                          : 'bg-[color:var(--gp-surface-muted)] gp-text-muted cursor-not-allowed'
                      }`}
                    >
                      <FontAwesomeIcon icon={isPageRTL ? faChevronRight : faChevronLeft} />
                      <span>{t('exam.previous')}</span>
                    </button>
                    
                    <div className={`text-sm gp-text-muted font-medium ${isPageRTL ? 'text-right' : 'text-left'}`}>
                      {t('exam.question')} {resultQuestionIndex + 1} {t('exam.of')} {totalFiltered}
                    </div>
                    
                    <button
                      onClick={() => {
                        if (canGoNext) {
                          setResultQuestionIndex(resultQuestionIndex + 1);
                          // Scroll to question review section
                          setTimeout(() => {
                            const element = document.getElementById('question-review-section');
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 100);
                        }
                      }}
                      disabled={!canGoNext}
                      className={`flex items-center ${isPageRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'} px-4 py-2 rounded-lg font-medium transition-all ${
                        canGoNext
                          ? 'bg-[color:var(--gp-brand)] hover:bg-[color:var(--gp-brand-hover)] text-white shadow-md hover:'
                          : 'bg-[color:var(--gp-surface-muted)] gp-text-muted cursor-not-allowed'
                      }`}
                    >
                      <span>{t('exam.next')}</span>
                      <FontAwesomeIcon icon={isPageRTL ? faChevronLeft : faChevronRight} />
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>


          {/* Action Buttons */}
          <div className={`flex flex-col sm:flex-row justify-center gap-3 ${isPageRTL ? 'flex-row-reverse' : ''}`} dir={rtlDir}>
            <div className={`flex items-center gap-3 ${isPageRTL ? 'flex-row-reverse' : ''}`}>
              <button 
                onClick={() => navigate(user ? '/dashboard' : '/')} 
                className={`btn-primary flex items-center justify-center ${isPageRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'} px-6 py-2.5 text-sm`}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-xs" style={{ transform: isPageRTL ? 'scaleX(-1)' : 'none' }} />
                <span>{user ? t('common.backToDashboard') : t('common.backToHome')}</span>
              </button>
              
              {/* Admin Edit Button */}
              {user?.isAdmin === true && exam && (
                <Link
                  to={`/admin/certifications?edit=${exam.id}`}
                  className={`inline-flex items-center gp-text-muted hover:text-[color:var(--gp-brand)] font-medium text-sm transition-colors px-4 py-2.5 rounded-lg border border-[color:var(--gp-border)] hover:border-[color:var(--gp-brand)] ${isPageRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2'}`}
                >
                  <FontAwesomeIcon icon={faEdit} className="text-xs" />
                  <span>{t('common.edit')}</span>
                </Link>
              )}
            </div>
            {!isGuest && (
              <button 
                onClick={() => {
                  setShowResult(false);
                  setResult(null);
                  startExam(mode!, questions.length);
                }} 
                className="btn-secondary flex items-center justify-center space-x-2 px-6 py-2.5 text-sm"
              >
                <FontAwesomeIcon icon={faRedo} className="text-xs" />
                <span>{mode === 'EXAM' ? t('exam.retakeExam') : t('exam.retakePractice')}</span>
              </button>
            )}
            {isGuest && (
              <Link 
                to="/register" 
                state={{ examId: examId }}
                className="btn-secondary flex items-center justify-center space-x-2 px-6 py-2.5 text-sm"
              >
                <FontAwesomeIcon icon={faCrown} className="text-xs" />
                <span>{t('exam.subscribeToUnlockMore')}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Use English question if toggle is on, otherwise use translated question
  const displayQuestions = (showEnglish && englishQuestions.length > 0 && mode === 'PRACTICE') 
    ? englishQuestions 
    : questions;
  
  // Find the current question by ID when switching languages
  const getCurrentQuestionIndex = () => {
    if (showEnglish && englishQuestions.length > 0 && mode === 'PRACTICE') {
      // When showing English, find the question with the same ID as the current translated question
      const currentQuestionId = questions[currentQuestionIndex]?.id;
      if (currentQuestionId) {
        const englishIndex = englishQuestions.findIndex(q => q.id === currentQuestionId);
        return englishIndex !== -1 ? englishIndex : currentQuestionIndex;
      }
    }
    return currentQuestionIndex;
  };
  
  const effectiveQuestionIndex = getCurrentQuestionIndex();
  const currentQuestion = displayQuestions[effectiveQuestionIndex];
  
  // Determine if we should use RTL: only if Arabic AND not showing English
  const isRTL = i18n.language === 'ar' && !showEnglish;
  
  // RTL classes helper
  const rtlClass = (ltrClass: string, rtlClass: string) => isPageRTL ? rtlClass : ltrClass;
  
  if (!currentQuestion) {
    return (
      <>
        {upgradeWallElement}
        <div className="min-h-screen flex items-center justify-center">
          <p className="gp-text-muted">No questions available</p>
        </div>
      </>
    );
  }

  const hasAnswered = mode === 'PRACTICE' && feedback[currentQuestion.id] !== undefined;
  const correctAnswers = currentQuestion.correct_answer_keys || 
    (currentQuestion.correct_answer_key ? [currentQuestion.correct_answer_key] : []);
  const currentAnswer = answers[currentQuestion.id];
  const userAnswers = Array.isArray(currentAnswer) ? currentAnswer : (currentAnswer ? [currentAnswer] : []);
  const selectedCorrect = correctAnswers.filter(ca => userAnswers.includes(ca));
  const isCorrect = hasAnswered && mode === 'PRACTICE' && 
    correctAnswers.length === selectedCorrect.length && userAnswers.length === correctAnswers.length;
  const isWrong = hasAnswered && mode === 'PRACTICE' && !isCorrect;

  console.log('ExamPage render:', { showUpgradeWall, exam: !!exam, upgradeReason });

  return (
    <>
      {upgradeWallElement}
      <div className="gp-page py-5 sm:py-6" dir={rtlDir}>
        <div className="gp-container">
        {/* Header Bar with Progress */}
        <div className={`card mb-4  ${isPageRTL ? 'border-r-4' : 'border-l-4'} relative overflow-hidden ${
          mode === 'PRACTICE' 
            ? isPageRTL
              ? 'bg-[color:var(--gp-brand-soft)] border-r-[color:var(--gp-brand)]'
              : 'bg-[color:var(--gp-brand-soft)] border-l-[color:var(--gp-brand)]'
            : isPageRTL
            ? 'bg-[color:var(--gp-accent-soft)] border-r-[color:var(--gp-accent)]'
            : 'bg-[color:var(--gp-accent-soft)] border-l-[color:var(--gp-accent)]'
        }`} dir={rtlDir}>
          <div className={`absolute ${isPageRTL ? 'top-0 left-0' : 'top-0 right-0'} w-32 h-32 bg-gradient-to-br from-[color:var(--gp-brand-soft)] to-transparent ${isPageRTL ? 'rounded-br-full' : 'rounded-bl-full'}`}></div>
          <div className="relative z-10">
            {/* Title and Timer Row */}
            <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-3 ${isPageRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center ${isPageRTL ? 'space-x-reverse space-x-2 sm:space-x-4 flex-row-reverse' : 'space-x-2 sm:space-x-4'} flex-1 min-w-0`}>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shadow-md flex-shrink-0 ${
                  mode === 'PRACTICE'
                    ? 'bg-[linear-gradient(135deg,var(--gp-brand),#17838d)]'
                    : 'bg-[linear-gradient(135deg,var(--gp-accent),#a34a1c)]'
                }`}>
                  <FontAwesomeIcon icon={mode === 'PRACTICE' ? faBook : faGraduationCap} className="text-white text-sm sm:text-lg" />
                </div>
                <div className={`min-w-0 flex-1 ${isPageRTL ? 'text-right' : 'text-left'}`}>
                  <h2 className={`text-base sm:text-lg font-bold text-[color:var(--gp-ink)] mb-0.5 truncate ${isPageRTL ? 'text-right' : 'text-left'}`}>{exam.name}</h2>
                  {exam.description && (
                    <p className={`text-xs gp-text-muted line-clamp-1 ${isPageRTL ? 'text-right' : 'text-left'}`}>{exam.description}</p>
                  )}
                </div>
                <span
                  className={`px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide shadow-sm flex-shrink-0 ${
                    mode === 'PRACTICE'
                      ? 'bg-[color:var(--gp-brand-soft)] text-[color:var(--gp-brand)] border border-[color:var(--gp-border)]'
                      : 'bg-[color:var(--gp-accent-soft)] text-[color:var(--gp-accent)] border border-[color:var(--gp-border)]'
                  }`}
                >
                  {mode === 'PRACTICE' ? 'Practice' : 'Exam'}
                </span>
              </div>
              {timeRemaining !== null && (mode === 'EXAM' || (mode === 'PRACTICE' && exam?.practice_mode_enabled)) && (
                <div
                  className={`flex items-center ${isPageRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'} px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg font-bold text-sm sm:text-base shadow-md transition-all flex-shrink-0 ${
                    timeRemaining < 300
                      ? 'bg-gradient-to-r from-red-500 to-red-600 text-white animate-pulse border-2 border-red-400 dark:border-red-600'
                      : mode === 'EXAM'
                      ? 'bg-[color:var(--gp-accent-soft)] text-[color:var(--gp-accent)] border border-[color:var(--gp-accent)]'
                      : 'bg-[color:var(--gp-brand-soft)] text-[color:var(--gp-brand)] border border-[color:var(--gp-brand)]'
                  }`}
                >
                  <FontAwesomeIcon icon={faClock} className="text-xs sm:text-sm" />
                  <span>{formatTime(timeRemaining)}</span>
                </div>
              )}
            </div>
            
            {/* Progress Bar */}
            <div className={`border-t border-[color:var(--gp-border)] pt-3`} dir={rtlDir}>
              <div className={`flex items-center justify-between mb-2 ${isPageRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center ${isPageRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'}`}>
                  <FontAwesomeIcon icon={mode === 'PRACTICE' ? faBook : faGraduationCap} className={`text-xs ${
                    mode === 'PRACTICE' ? 'text-[color:var(--gp-brand)]' : 'text-[color:var(--gp-accent)]'
                  }`} />
                  <span className={`text-xs font-semibold text-[color:var(--gp-ink-muted)] ${isPageRTL ? 'text-right' : 'text-left'}`}>
                    Question <span className="font-bold text-[color:var(--gp-ink)]">{effectiveQuestionIndex + 1}</span> of <span className="font-bold text-[color:var(--gp-ink)]">{displayQuestions.length}</span>
                  </span>
                </div>
                <div className={`px-2.5 py-1 rounded-md text-xs font-bold ${
                  mode === 'PRACTICE'
                    ? 'bg-[color:var(--gp-brand-soft)] text-[color:var(--gp-brand)]'
                    : 'bg-[color:var(--gp-accent-soft)] text-[color:var(--gp-accent)]'
                }`}>
                  {Math.round(((effectiveQuestionIndex + 1) / displayQuestions.length) * 100)}%
                </div>
              </div>
              <div className="w-full bg-[color:var(--gp-surface-muted)] rounded-full h-2 overflow-hidden shadow-inner">
                <div
                  className={`h-2 rounded-full transition-all duration-500 shadow-sm ${
                    mode === 'PRACTICE'
                      ? 'bg-[color:var(--gp-brand)]'
                      : 'bg-[color:var(--gp-accent)]'
                  }`}
                  style={{ width: `${((effectiveQuestionIndex + 1) / displayQuestions.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Question Container */}
        <div className={`card mb-4  border-2 border-[color:var(--gp-border)] ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
          <div className={`flex items-center justify-between mb-4 pb-3 border-b-2 border-[color:var(--gp-border)] ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-sm ${
                mode === 'PRACTICE'
                  ? 'bg-[linear-gradient(135deg,var(--gp-brand),#17838d)]'
                  : 'bg-[linear-gradient(135deg,var(--gp-accent),#a34a1c)]'
              }`}>
                <FontAwesomeIcon icon={faQuestionCircle} className="text-white text-sm" />
              </div>
              <div>
                <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide border ${
                  mode === 'PRACTICE'
                    ? 'bg-[color:var(--gp-brand-soft)] text-[color:var(--gp-brand)] border-[color:var(--gp-border)]'
                    : 'bg-[color:var(--gp-accent-soft)] text-[color:var(--gp-accent)] border-[color:var(--gp-border)]'
                }`}>
                  {currentQuestion.topic}
                </span>
                {currentQuestion.difficulty && (
                  <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-xs gp-text-muted font-medium`}>
                    {'★'.repeat(currentQuestion.difficulty)}{'☆'.repeat(5 - currentQuestion.difficulty)}
                  </span>
                )}
              </div>
            </div>
            <div className={`flex items-center gap-2 ${isPageRTL ? 'flex-row-reverse' : ''}`}>
              {/* English Toggle - Only show in Practice mode and when language is not English */}
              {mode === 'PRACTICE' && i18n.language !== 'en' && englishQuestions.length > 0 && (
                <button
                  onClick={() => {
                    // When switching languages, find the same question by ID in the other array
                    const currentQuestionId = displayQuestions[effectiveQuestionIndex]?.id;
                    if (currentQuestionId) {
                      const targetArray = showEnglish ? questions : englishQuestions;
                      const targetIndex = targetArray.findIndex(q => q.id === currentQuestionId);
                      if (targetIndex !== -1) {
                        setCurrentQuestionIndex(targetIndex);
                      }
                    }
                    setShowEnglish(!showEnglish);
                  }}
                  className={`flex items-center ${isPageRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} px-3 py-2 rounded-lg transition-all font-medium text-xs ${
                    showEnglish
                      ? 'bg-[color:var(--gp-brand-soft)] text-[color:var(--gp-brand)] shadow-md border border-[color:var(--gp-brand)]'
                      : 'bg-[color:var(--gp-surface-muted)] text-[color:var(--gp-ink-muted)] hover:opacity-90 border border-[color:var(--gp-border)]'
                  }`}
                  title={showEnglish ? t('exam.showTranslated', 'Show in {{language}}', { language: i18n.language.toUpperCase() }) : t('exam.showEnglish', 'Show in English')}
                >
                  <FontAwesomeIcon icon={faLanguage} className="text-xs" />
                  <span>{showEnglish ? 'EN' : i18n.language.toUpperCase()}</span>
                </button>
              )}
              <button
                onClick={() => toggleFlag(currentQuestion.id)}
                className={`flex items-center ${isPageRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} px-3 py-2 rounded-lg transition-all font-medium ${
                  flaggedQuestions.has(currentQuestion.id)
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 shadow-md border-2 border-yellow-300 dark:border-yellow-700'
                    : 'bg-[color:var(--gp-surface-muted)] text-[color:var(--gp-ink-muted)] hover:opacity-90 border border-[color:var(--gp-border)]'
                }`}
              >
                <FontAwesomeIcon icon={faFlag} className="text-sm" />
                <span className="text-xs">{flaggedQuestions.has(currentQuestion.id) ? t('exam.flagged') : t('exam.flag')}</span>
              </button>
            </div>
          </div>

          <div className={`text-lg font-semibold text-[color:var(--gp-ink)] mb-6 leading-relaxed px-1 ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <MarkdownRenderer content={currentQuestion.question_text} />
          </div>

          <div className={`space-y-2 mb-4 ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
            {Object.entries(currentQuestion.options).map(([key, value]) => {
              const currentAnswer = answers[currentQuestion.id];
              const isSelected = Array.isArray(currentAnswer) 
                ? currentAnswer.includes(key)
                : currentAnswer === key;
              
              const correctAnswers = currentQuestion.correct_answer_keys || 
                (currentQuestion.correct_answer_key ? [currentQuestion.correct_answer_key] : []);
              const showCorrect = hasAnswered && correctAnswers.includes(key);
              const showWrong = hasAnswered && isSelected && !correctAnswers.includes(key);
              
              const hasMultipleAnswers = correctAnswers.length > 1;

              return (
                <button
                  key={key}
                  onClick={() => handleAnswer(currentQuestion.id, key)}
                  disabled={hasAnswered}
                  className={`w-full ${isRTL ? 'text-right' : 'text-left'} p-3 rounded-lg border-2 transition-all duration-200 group ${
                    isSelected
                      ? showCorrect
                        ? 'bg-green-50 border-green-500 dark:bg-green-900/20 dark:border-green-500 shadow-md shadow-green-100 dark:shadow-green-900/20'
                        : showWrong
                        ? 'bg-red-50 border-red-500 dark:bg-red-900/20 dark:border-red-500 shadow-md shadow-red-100 dark:shadow-red-900/20'
                        : 'bg-[color:var(--gp-brand-soft)] border-[color:var(--gp-brand)]'
                      : showCorrect
                      ? 'bg-green-50/50 border-green-300 dark:bg-green-900/10 dark:border-green-700'
                      : 'bg-[color:var(--gp-surface)] border-[color:var(--gp-border)] hover:border-[color:var(--gp-brand)] hover:bg-[color:var(--gp-brand-soft)] text-[color:var(--gp-ink)]'
                  } ${hasAnswered ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center font-semibold text-xs transition-colors ${
                      isSelected
                        ? showCorrect
                          ? 'bg-green-500 text-white'
                          : showWrong
                          ? 'bg-red-500 text-white'
                          : 'bg-[color:var(--gp-brand)] text-white'
                        : showCorrect
                        ? 'bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200'
                        : 'bg-[color:var(--gp-surface-muted)] text-[color:var(--gp-ink-muted)] group-hover:bg-[color:var(--gp-brand-soft)] group-hover:text-[color:var(--gp-brand)]'
                    }`}>
                      {key}
                    </div>
                    <span className={`flex-1 pt-0.5 text-sm leading-relaxed ${
                      isSelected
                        ? showCorrect
                          ? 'text-green-900 dark:text-green-100 font-medium'
                          : showWrong
                          ? 'text-red-900 dark:text-red-100 font-medium'
                          : 'text-[color:var(--gp-brand)] font-medium'
                        : showCorrect
                        ? 'text-green-800 dark:text-green-200'
                        : 'text-[color:var(--gp-ink-muted)]'
                    }`}>
                      <MarkdownRenderer content={value} />
                    </span>
                    {showCorrect && (
                      <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    )}
                    {showWrong && (
                      <FontAwesomeIcon icon={faTimesCircle} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Show message when user needs to select more answers for multiple-choice questions */}
          {mode === 'PRACTICE' && !hasAnswered && correctAnswers.length > 1 && userAnswers.length > 0 && userAnswers.length < correctAnswers.length && (
            <div className={`p-3 rounded-lg mb-4 bg-[color:var(--gp-brand-soft)] ${isRTL ? 'border-r-4 border-r-[color:var(--gp-brand)]' : 'border-l-4 border-l-[color:var(--gp-brand)]'}`} dir={isRTL ? 'rtl' : 'ltr'}>
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <FontAwesomeIcon icon={faInfoCircle} className="text-[color:var(--gp-brand)] text-sm" />
                <p className={`text-sm text-[color:var(--gp-ink)] font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                  Please select {correctAnswers.length - userAnswers.length} more answer{correctAnswers.length - userAnswers.length > 1 ? 's' : ''} ({userAnswers.length} of {correctAnswers.length} selected)
                </p>
              </div>
            </div>
          )}

          {hasAnswered && feedback[currentQuestion.id] && (
            <div
              className={`p-4 rounded-lg mb-4 ${isRTL ? 'border-r-4' : 'border-l-4'} shadow-sm ${
                feedback[currentQuestion.id].correct
                  ? isRTL
                    ? 'bg-green-50 dark:bg-green-900/20 border-r-green-500 dark:border-r-green-400'
                    : 'bg-green-50 dark:bg-green-900/20 border-l-green-500 dark:border-l-green-400'
                  : isRTL
                  ? 'bg-red-50 dark:bg-red-900/20 border-r-red-500 dark:border-r-red-400'
                  : 'bg-red-50 dark:bg-red-900/20 border-l-red-500 dark:border-l-red-400'
              }`}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <div className={`flex items-center mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {feedback[currentQuestion.id].correct ? (
                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-white text-xs" />
                    </div>
                    <p className={`font-bold text-sm text-green-800 dark:text-green-200 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('exam.correctAnswerExclamation')}
                    </p>
                  </div>
                ) : (
                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                      <FontAwesomeIcon icon={faTimesCircle} className="text-white text-xs" />
                    </div>
                    <p className={`font-bold text-sm text-red-800 dark:text-red-200 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {t('exam.incorrectAnswer')}
                    </p>
                  </div>
                )}
              </div>
              {feedback[currentQuestion.id].explanation && (
                <div className="mt-3 pt-3 border-t border-[color:var(--gp-border)]">
                  <div className={`flex items-start gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <FontAwesomeIcon icon={faInfoCircle} className="text-[color:var(--gp-brand)] mt-0.5 flex-shrink-0 text-sm" />
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <strong className="text-[color:var(--gp-ink)] block mb-1 text-sm">{t('exam.explanationLabel')}</strong>
                      <div className={`text-[color:var(--gp-ink-muted)] text-sm leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                        <MarkdownRenderer content={feedback[currentQuestion.id].explanation} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t-2 border-[color:var(--gp-border)] ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
            <button
              onClick={() => setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))}
              disabled={currentQuestionIndex === 0}
              className={`btn-secondary flex items-center justify-center ${i18n.language === 'ar' ? 'space-x-2' : 'space-x-2'} disabled:opacity-50 disabled:cursor-not-allowed px-4 sm:px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                currentQuestionIndex === 0 ? '' : i18n.language === 'ar' ? 'hover:shadow-md hover:translate-x-0.5' : 'hover:shadow-md hover:-translate-x-0.5'
              }`}
            >
              {i18n.language === 'ar' ? (
                <>
                  <span>{t('exam.previous')}</span>
                  <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                  <span>{t('exam.previous')}</span>
                </>
              )}
            </button>

            <div className={`flex items-center justify-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} text-xs gp-text-muted px-2`}>
              <span className="font-medium text-center">
                {t('exam.ofAnswered', { answered: Object.keys(answers).length, total: displayQuestions.length })}
              </span>
            </div>

            {effectiveQuestionIndex === displayQuestions.length - 1 ? (
              <button 
                onClick={handleSubmit} 
                className={`btn-primary flex items-center justify-center ${isRTL ? 'space-x-reverse space-x-2 flex-row-reverse' : 'space-x-2'} px-4 sm:px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  mode === 'EXAM' 
                    ? ''
                    : ''
                }`}
              >
                <span>{mode === 'EXAM' ? 'Submit Exam' : 'Submit Practice'}</span>
                <FontAwesomeIcon icon={faCheckCircle} className="text-xs" />
              </button>
            ) : (
              <button
                onClick={() => {
                  const newIndex = Math.min(displayQuestions.length - 1, effectiveQuestionIndex + 1);
                  // Find the question ID at the new index in current array
                  const newQuestionId = displayQuestions[newIndex]?.id;
                  if (newQuestionId) {
                    // Find the same question ID in the base questions array to sync index
                    const baseIndex = questions.findIndex(q => q.id === newQuestionId);
                    if (baseIndex !== -1) {
                      setCurrentQuestionIndex(baseIndex);
                    } else {
                      setCurrentQuestionIndex(newIndex);
                    }
                  } else {
                    setCurrentQuestionIndex(newIndex);
                  }
                }}
                className={`btn-secondary flex items-center justify-center ${isRTL ? 'space-x-2' : 'space-x-2'} px-4 sm:px-5 py-2.5 rounded-lg text-sm font-semibold ${isRTL ? 'hover:shadow-md hover:-translate-x-0.5' : 'hover:shadow-md hover:translate-x-0.5'} transition-all`}
              >
                {isRTL ? (
                  <>
                    <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                    <span>{t('exam.next')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('exam.next')}</span>
                    <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                  </>
                )}
              </button>
            )}
          </div>

          {/* Question Navigator - Collapsible Card */}
          {(mode === 'PRACTICE' || mode === 'EXAM') && (
            <div className="mt-4">
              <button
                onClick={() => setShowQuestionNavigator(!showQuestionNavigator)}
                className={`w-full flex items-center ${isRTL ? 'flex-row-reverse justify-between' : 'justify-between'} p-4 rounded-lg border-2 transition-all ${
                  mode === 'PRACTICE'
                    ? 'bg-[color:var(--gp-brand-soft)] border-[color:var(--gp-border)] hover:opacity-90'
                    : 'bg-[color:var(--gp-accent-soft)] border-[color:var(--gp-border)] hover:opacity-90'
                }`}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3 flex-row-reverse' : 'space-x-3'}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    mode === 'PRACTICE'
                      ? 'bg-[linear-gradient(135deg,var(--gp-brand),#17838d)]'
                      : 'bg-[linear-gradient(135deg,var(--gp-accent),#a34a1c)]'
                  }`}>
                    <FontAwesomeIcon icon={faList} className="text-white text-sm" />
                  </div>
                  <div className={isRTL ? 'text-right' : 'text-left'}>
                    <span className="font-bold text-sm text-[color:var(--gp-ink)] block">
                      {t('exam.questionNavigator')}
                    </span>
                    <span className="text-xs gp-text-muted">
                      {t('exam.ofAnswered', { answered: Object.keys(answers).length, total: questions.length })}
                    </span>
                  </div>
                </div>
                <FontAwesomeIcon 
                  icon={showQuestionNavigator ? faChevronUp : faChevronDown} 
                  className={`text-xs transition-transform ${
                    mode === 'PRACTICE'
                      ? 'text-[color:var(--gp-brand)]'
                      : 'text-[color:var(--gp-accent)]'
                  } ${showQuestionNavigator ? 'rotate-180' : ''}`}
                />
              </button>
              
              {showQuestionNavigator && (
                <div className="mt-2 p-3 bg-[color:var(--gp-surface)] rounded-lg border border-[color:var(--gp-border)]">
                  <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-1.5">
                    {questions.map((question, index) => {
                      const isAnswered = answers[question.id] !== undefined;
                      const isFlagged = flaggedQuestions.has(question.id);
                      const isCurrent = question.id === currentQuestion?.id;
                      
                      return (
                        <button
                          key={question.id}
                          onClick={() => {
                            setCurrentQuestionIndex(index);
                            setShowQuestionNavigator(false); // Close navigator after selection
                          }}
                          className={`w-9 h-9 rounded-lg text-xs font-medium transition-all relative ${
                            isCurrent
                              ? 'bg-[color:var(--gp-brand)] text-white ring-2 ring-[color:var(--gp-brand)] ring-offset-1'
                              : isAnswered
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : isFlagged
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                              : 'bg-[color:var(--gp-surface-muted)] text-[color:var(--gp-ink-muted)] hover:opacity-90'
                          }`}
                          title={`${t('exam.question')} ${index + 1}${isAnswered ? ` - ${t('exam.answered')}` : ''}${isFlagged ? ` - ${t('exam.flagged')}` : ''}`}
                        >
                          {index + 1}
                          {isAnswered && (
                            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-[color:var(--gp-surface)]"></span>
                          )}
                          {isFlagged && !isAnswered && (
                            <span className="absolute -top-0.5 -right-0.5">
                              <FontAwesomeIcon icon={faFlag} className="text-yellow-600 dark:text-yellow-400 text-[10px]" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-3 pt-3 border-t border-[color:var(--gp-border)] flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1.5">
                        <div className="w-3 h-3 bg-green-100 dark:bg-green-900/30 rounded"></div>
                        <span className="gp-text-muted">{t('exam.answered')}</span>
                      </div>
                      <div className="flex items-center space-x-1.5">
                        <div className="w-3 h-3 bg-yellow-100 dark:bg-yellow-900/30 rounded"></div>
                        <span className="gp-text-muted">{t('exam.flagged')}</span>
                      </div>
                      <div className="flex items-center space-x-1.5">
                        <div className="w-3 h-3 bg-[color:var(--gp-brand)] rounded"></div>
                        <span className="gp-text-muted">{t('exam.current')}</span>
                      </div>
                    </div>
                    <div className="gp-text-muted">
                      {t('exam.ofAnswered', { answered: Object.keys(answers).length, total: questions.length })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </>
  );
};

export default ExamPage;
